import { InterstitialIntroFrame } from '@/api/generated-catch-backend/model';
import { animate, motion, useMotionValue } from 'motion/react';
import { useEffect } from 'react';
import { InterstitialIntro as InterstitialIntroType } from '../../../types';
import { NextButton } from '../next-button/button';
import {
  BackgroundAsset,
  FRAME_TRANSITION_DURATION,
  getAnimationType,
  getGraphicStyles,
  GraphicContent,
  TextContent,
  useAutoAdvance,
  useFrameNavigation,
} from './interstitial-shared';

type InterstitialIntroProps = {
  interstitial: InterstitialIntroType;
  onNext: () => void;
};

const ContentRenderer = ({
  interstitial,
  frame,
  frameIndex,
  animationType,
  onTransitionComplete,
  onNext,
}: {
  interstitial: InterstitialIntroType;
  frame: InterstitialIntroFrame;
  frameIndex: number;
  animationType: { initial: any; animate: any };
  onTransitionComplete?: () => void;
  onNext: () => void;
}) => {
  const graphicStyles = frame.graphic ? getGraphicStyles(frame.graphic) : {};
  const content = (
    <>
      <TextContent frame={frame} textType="above" classNamePrefix="interstitial-intro" />
      <div style={graphicStyles}>
        <GraphicContent
          interstitial={interstitial}
          frame={frame}
          classNamePrefix="interstitial-intro"
          onNext={onNext}
        />
      </div>
      <TextContent frame={frame} textType="below" classNamePrefix="interstitial-intro" />
    </>
  );

  const buttonText = frame.ctaText || 'Next';

  // Define card styles based on card type
  const getCardStyles = (cardType: string | null | undefined) => {
    switch (cardType) {
      case 'glass': // Glass
        return {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '32px',
          opacity: 1.0,
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        };
      case 'transparent': // Transparent
        return {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '16px',
          padding: '32px',
          opacity: 1.0,
          backdropFilter: 'blur(8px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
        };
      case 'none': // None
      case null:
      case undefined:
      default:
        return null;
    }
  };

  const cardStyles = getCardStyles(frame.card as unknown as string);

  if (cardStyles && frame.card !== 'none') {
    return (
      <motion.div
        key={frameIndex}
        className="interstitial-intro-card"
        initial={animationType.initial}
        animate={animationType.animate}
        transition={{
          duration: FRAME_TRANSITION_DURATION,
          ease: 'easeOut',
        }}
        style={{
          willChange: 'transform, opacity',
          ...cardStyles,
        }}
        {...(onTransitionComplete && { onAnimationComplete: onTransitionComplete })}
      >
        {content}
        <NextButton variant="primary" text={buttonText} onNext={onNext} style={{ marginTop: '24px' }} />
      </motion.div>
    );
  }

  return (
    <motion.div
      key={frameIndex}
      className="interstitial-intro-content"
      initial={animationType.initial}
      animate={animationType.animate}
      transition={{
        duration: FRAME_TRANSITION_DURATION,
        ease: 'easeOut',
      }}
      style={{ willChange: 'transform, opacity' }}
      {...(onTransitionComplete && { onAnimationComplete: onTransitionComplete })}
    >
      {content}
    </motion.div>
  );
};

export const InterstitialIntro = ({ interstitial, onNext }: InterstitialIntroProps) => {
  const frames = interstitial.frames;
  const { currentFrameIndex, setCurrentFrameIndex, handleNext, currentFrame } = useFrameNavigation(frames, onNext);
  const progress = useMotionValue(0);

  // Use shared auto-advance logic
  useAutoAdvance(frames, currentFrameIndex, setCurrentFrameIndex);

  // Progress tracking logic (intro-specific)
  useEffect(() => {
    if (frames.length <= 1) {
      animate(progress, 0, { duration: 0.2 });
      return;
    }

    const currentFrame = frames[currentFrameIndex];

    // Check if current frame should auto-advance
    if (!currentFrame.autoAdvance) {
      animate(progress, 0, { duration: 0.2 });
      return;
    }

    // Use explicit fallback for auto_advance_time (default to 3 seconds)
    const advanceTime =
      currentFrame.autoAdvanceTime && currentFrame.autoAdvanceTime > 0 ? currentFrame.autoAdvanceTime : 3;

    let animationControls: ReturnType<typeof animate> | null = null;

    const startProgress = () => {
      animationControls = animate(progress, 1, {
        duration: advanceTime,
        ease: 'linear',
      });
    };

    // Start progress animation
    startProgress();

    return () => {
      if (animationControls) {
        animationControls.stop();
      }
    };
  }, [frames, currentFrameIndex, progress]);

  if (frames.length === 0) {
    return null;
  }

  const animationType = getAnimationType(currentFrame.animateIn);

  return (
    <div className="interstitial-intro-container">
      <div className="interstitial-intro-background" />

      <BackgroundAsset backgroundAsset={currentFrame.backgroundAsset} classNamePrefix="interstitial-intro" />

      <div
        className="interstitial-intro-overlay"
        style={{
          opacity: currentFrame.backgroundOpacity ?? 0.6,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)', // Safari support
        }}
      />

      <div className="interstitial-intro-layout">
        <div className="interstitial-intro-spacer" />

        <ContentRenderer
          interstitial={interstitial}
          frame={currentFrame}
          frameIndex={currentFrameIndex}
          animationType={animationType}
          onNext={handleNext}
        />

        {/* Only show button for overlay mode (not when card is present) */}
        <div className="interstitial-intro-bottom pb-safe-offset-16 md:pb-safe-offset-4">
          {(!currentFrame.card || currentFrame.card === 'none') && (
            <NextButton text={currentFrame.ctaText || 'Next'} onNext={handleNext} />
          )}
        </div>

        <div className="interstitial-intro-spacer" />
      </div>
    </div>
  );
};
